<template>
  <div>
    <b-card title="Треки без артиста">
      <div v-if="total > 0" class="mb-2">
        <h2 class="text-primary mb-0">
          {{ total.toLocaleString('ru-RU') }}
        </h2>
        <small class="text-muted">треков с найденным артистом</small>
      </div>
      <b-overlay
          :show="isLoading"
          rounded="sm"
      >
        <b-table
            ref="refOrphanTracksTable"
            :items="tracks"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            empty-text="Нет треков"
            class="position-relative"
        >

          <!-- Column: Artist -->
          <template #cell(artist)="data">
            <b-link
                :to="{ name: 'artist-view', params: { id: data.item.artist.id }}"
                class="font-weight-bold"
            >
              {{ data.item.artist.title }}
            </b-link>
          </template>

          <!-- Column: Matched By -->
          <template #cell(matched_by)="data">
            <b-badge :variant="data.item.matched_by === 'title' ? 'light-success' : 'light-info'">
              {{ data.item.matched_by === 'title' ? 'имя' : `алиас: ${data.item.alias}` }}
            </b-badge>
          </template>

          <!-- Column: File -->
          <template #cell(has_file)="data">
            <feather-icon
                :icon="data.item.has_file ? 'CheckCircleIcon' : 'XCircleIcon'"
                :class="data.item.has_file ? 'text-success' : 'text-danger'"
                size="20"
            />
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <span class="text-nowrap">
              <b-badge v-if="data.item.is_duplicate" variant="light-danger" class="mr-50">
                дубль
              </b-badge>
              <b-button
                  v-if="!data.item.is_duplicate"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  size="sm"
                  :disabled="processingId === data.item.id"
                  @click="bindTrack(data.item)"
              >
                Привязать
              </b-button>
              <b-button
                  v-else
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="outline-danger"
                  size="sm"
                  :disabled="processingId === data.item.id"
                  @click="deleteTrack(data.item)"
              >
                Удалить
              </b-button>
            </span>
          </template>
        </b-table>

        <b-pagination
            v-if="total > perPage"
            v-model="page"
            :total-rows="total"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mt-1 mb-0"
        >
          <template #prev-text>
            <feather-icon
                icon="ChevronLeftIcon"
                size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
                icon="ChevronRightIcon"
                size="18"
            />
          </template>
        </b-pagination>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BCard,
  BTable,
  BButton,
  BLink,
  BOverlay,
  BPagination,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import store from "@/store";
import musicStoreModule from "@/views/db-music/musicStoreModule";
import {onUnmounted, ref, watch} from "@vue/composition-api";
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BBadge,
    BCard,
    BTable,
    BButton,
    BLink,
    BOverlay,
    BPagination,
  },
  directives: {
    Ripple,
  },
  setup() {
    const STORE_MODULE_NAME = 'orphan-tracks'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const tracks = ref([])
    const isLoading = ref(false)
    const page = ref(1)
    const perPage = ref(50)
    const total = ref(0)
    const processingId = ref(null)

    const tableColumns = [
      {key: 'id', label: '#', sortable: false},
      {key: 'title', label: 'Название трека', sortable: false},
      {key: 'artist', label: 'Артист', sortable: false},
      {key: 'matched_by', label: 'Совпадение', sortable: false},
      {key: 'play_count', label: 'Прослушивания', sortable: false},
      {key: 'has_file', label: 'Файл', sortable: false},
      {key: 'actions', label: 'Действия'},
    ]

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    }

    const errorMessage = (error, fallback) => error.response?.data?.message || fallback

    const fetchOrphanTracks = async () => {
      isLoading.value = true
      try {
        const res = await store.dispatch('orphan-tracks/fetchOrphanTracks', {
          page: page.value,
          per_page: perPage.value,
        })
        tracks.value = res.data.data
        total.value = res.data.meta.total
      } catch (error) {
        showToast('Ошибка загрузки треков', 'danger')
      } finally {
        isLoading.value = false
      }
    }

    const removeRow = (id) => {
      tracks.value = tracks.value.filter(track => track.id !== id)
      total.value -= 1
      if (tracks.value.length === 0 && page.value > 1) {
        page.value -= 1 // watch перезагрузит список
      } else if (tracks.value.length === 0 && total.value > 0) {
        fetchOrphanTracks()
      }
    }

    const bindTrack = async (item) => {
      processingId.value = item.id
      try {
        await store.dispatch('orphan-tracks/bindTrackArtist', {
          id: item.id,
          artistId: item.artist.id,
        })
        removeRow(item.id)
        showToast(`Трек привязан к «${item.artist.title}»`, 'success')
      } catch (error) {
        showToast(errorMessage(error, 'Ошибка привязки трека'), 'danger')
      } finally {
        processingId.value = null
      }
    }

    const deleteTrack = async (item) => {
      if (!confirm(`Удалить трек «${item.title}»?`)) return
      processingId.value = item.id
      try {
        await store.dispatch('orphan-tracks/deleteTrack', {id: item.id})
        removeRow(item.id)
        showToast('Трек удалён', 'success')
      } catch (error) {
        showToast(errorMessage(error, 'Ошибка удаления трека'), 'danger')
      } finally {
        processingId.value = null
      }
    }

    watch([page], () => {
      fetchOrphanTracks()
    })

    fetchOrphanTracks()

    return {
      tracks,
      isLoading,
      tableColumns,
      page,
      perPage,
      total,
      processingId,
      bindTrack,
      deleteTrack,
    }
  },
}
</script>
